import { BankOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Divider, Form, Image, Input, Row, Space, Typography, Upload, message } from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { API_URL, apiCall } from '../../API/RestApi';

const { Title, Text } = Typography;

function Company() {
  const token = useSelector((state) => state.user.token);
  const [form] = Form.useForm();
  const [company, setCompany] = useState(null);
  const [logo, setLogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const resolveLogoUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    const base = String(API_URL || '').replace(/\/$/, '');
    const path = String(value).startsWith('/') ? value : `/${value}`;
    return `${base}${path}`;
  };

  const loadCompany = async () => {
    setLoading(true);
    const res = await apiCall({
      method: 'GET',
      url: '/company-setting',
      data: { headers: { Authorization: `Bearer ${token}` } },
    });
    setLoading(false);
    if (res?.status === 'success') {
      setCompany(res.data || {});
      form.setFieldsValue(res.data || {});
    } else {
      message.error(res?.message || 'Data perusahaan belum dapat dimuat.');
    }
  };

  const saveCompany = async (values) => {
    const fd = new FormData();
    Object.entries(values || {}).forEach(([key, value]) => fd.append(key, value ?? ''));
    if (logo[0]?.originFileObj) fd.append('logo', logo[0].originFileObj);
    setLoading(true);
    setUploading(Boolean(logo[0]?.originFileObj));
    const res = await apiCall({
      method: 'PUT',
      url: '/company-setting',
      data: {
        data: fd,
        // Jangan set Content-Type secara manual. Browser/Axios akan menambahkan
        // multipart boundary yang diperlukan agar file benar-benar terbaca server.
        headers: { Authorization: `Bearer ${token}` },
      },
    });
    setLoading(false);
    setUploading(false);
    if (res?.status !== 'success') return message.error(res?.message || 'Company Profile belum dapat disimpan.');
    message.success(res.message || 'Company Profile berhasil disimpan.');
    setLogo([]);
    setCompany(res.data || {});
    form.setFieldsValue(res.data || {});
  };

  useEffect(() => {
    if (token) loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="responsive-page company-page">
      <Helmet><title>Company Profile</title></Helmet>
      <div style={{ marginBottom: 18 }}>
        <Text type="secondary">DATA MASTER</Text>
        <Title level={2} style={{ margin: '4px 0 0' }}>Company Profile</Title>
        <Text type="secondary">Kelola identitas perusahaan yang digunakan pada slip gaji PDF, email payroll, dan dokumen perusahaan.</Text>
      </div>

      <Card bordered={false} loading={loading && !company}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BankOutlined style={{ fontSize: 22 }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>Identitas Perusahaan</Title>
            <Text type="secondary">Pastikan informasi di bawah sesuai dengan identitas resmi perusahaan.</Text>
          </div>
        </div>
        <Divider />
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 18 }}
          message="Data ini menjadi sumber utama untuk header slip gaji dan email payroll."
          description="Perubahan nama, alamat, logo, atau kontak perusahaan akan otomatis digunakan pada slip gaji berikutnya."
        />

        <Form form={form} layout="vertical" onFinish={saveCompany}>
          <Row gutter={[16, 4]}>
            <Col xs={24} lg={16}>
              <Row gutter={12}>
                <Col xs={24} md={12}><Form.Item name="company_name" label="Nama Perusahaan" rules={[{ required: true, message: 'Nama perusahaan wajib diisi.' }]}><Input placeholder="PT Contoh Indonesia" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="slip_title" label="Judul Slip Gaji"><Input placeholder="SLIP GAJI" /></Form.Item></Col>
                <Col span={24}><Form.Item name="address" label="Alamat"><Input.TextArea rows={3} placeholder="Jalan, gedung, kawasan, nomor" /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="city" label="Kota"><Input /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="province" label="Provinsi"><Input /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="postal_code" label="Kode Pos"><Input /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="country" label="Negara"><Input /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="phone" label="Telepon"><Input /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="email" label="Email Perusahaan"><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="website" label="Website"><Input placeholder="www.perusahaan.com" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="tax_number" label="NPWP / Tax Number"><Input /></Form.Item></Col>
                <Col span={24}><Form.Item name="footer_text" label="Footer Slip Gaji"><Input.TextArea rows={3} placeholder="Catatan atau pesan yang ditampilkan di bagian bawah slip gaji" /></Form.Item></Col>
              </Row>
            </Col>
            <Col xs={24} lg={8}>
              <Card size="small" title="Logo Perusahaan" style={{ height: '100%' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div style={{ minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #d9d9d9', borderRadius: 10, padding: 14 }}>
                    {logo[0]?.originFileObj ? (
                      <Image src={URL.createObjectURL(logo[0].originFileObj)} preview width="100%" style={{ maxWidth: 220, maxHeight: 120, objectFit: 'contain' }} />
                    ) : company?.logo_url ? (
                      <Image src={resolveLogoUrl(company.logo_url)} preview width="100%" style={{ maxWidth: 220, maxHeight: 120, objectFit: 'contain' }} />
                    ) : <Text type="secondary">Logo belum tersedia</Text>}
                  </div>
                  <Upload
                    beforeUpload={(file) => {
                      const allowed = ['image/png', 'image/jpeg'].includes(file.type);
                      if (!allowed) {
                        message.error('Logo harus berupa PNG, JPG, atau JPEG.');
                        return Upload.LIST_IGNORE;
                      }
                      const max5Mb = file.size / 1024 / 1024 <= 5;
                      if (!max5Mb) {
                        message.error('Ukuran logo maksimal 5 MB.');
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    maxCount={1}
                    fileList={logo}
                    onChange={({ fileList }) => setLogo(fileList.slice(-1))}
                    onRemove={() => { setLogo([]); return true; }}
                  >
                    <Button block icon={<UploadOutlined />} loading={uploading}>Pilih Logo</Button>
                  </Upload>
                  {logo[0]?.name && <Text type="secondary" style={{ fontSize: 12 }}>File dipilih: {logo[0].name}</Text>}
                  <Text type="secondary" style={{ fontSize: 12 }}>PNG/JPG/JPEG, maksimal 5 MB. Logo disimpan ketika tombol Simpan Company Profile ditekan.</Text>
                </Space>
              </Card>
            </Col>
          </Row>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>Simpan Company Profile</Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}

export default Company;
